import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect, createContext, useContext, useMemo, useCallback } from "react";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Pricing from "./pages/Pricing";
import Checkout from "./pages/Checkout";
import Dashboard from "./pages/Dashboard";
import NewSearch from "./pages/NewSearch";
import SearchDetail from "./pages/SearchDetail";
import Searches from "./pages/Searches";
import Batches from "./pages/Batches";
import BatchDetail from "./pages/BatchDetail";
import AdminDashboard from "./pages/AdminDashboard";
import ImportCatalogue from "./pages/ImportCatalogue";
import HelpCenter from "./pages/HelpCenter";
import CodeLookup from "./pages/CodeLookup";
import CatalogueMatching from "./pages/CatalogueMatching";
import DashboardLayout from "./components/DashboardLayout";
import { FeedbackProvider } from "./contexts/FeedbackContext";
import { QuotaProvider } from "./contexts/QuotaContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Set up axios interceptor to automatically include auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("syncchain_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      const token = localStorage.getItem("syncchain_token");
      if (token) {
        // Check for legacy token
        if (token === "syncchain_session_active") {
          setIsAuthenticated(true);
          setUser({ legacy: true, is_admin: false });
          setLoading(false);
          return;
        }
        
        // Verify JWT token
        try {
          const response = await axios.get(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data.user);
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("syncchain_token");
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = useCallback((token, userData) => {
    localStorage.setItem("syncchain_token", token);
    setIsAuthenticated(true);
    setUser(userData || { legacy: true, is_admin: false });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("syncchain_token");
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const authValue = useMemo(() => ({
    isAuthenticated, user, login, logout, updateUser
  }), [isAuthenticated, user, login, logout, updateUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-syncchain-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-syncchain-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authValue}>
      <QuotaProvider>
      <FeedbackProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-syncchain-bg text-syncchain-text font-sans">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/search" element={<NewSearch />} />
                      <Route path="/search/:id" element={<SearchDetail />} />
                      <Route path="/searches" element={<Searches />} />
                      <Route path="/batches" element={<Batches />} />
                      <Route path="/batch/:id" element={<BatchDetail />} />
                      <Route path="/import-catalogue" element={<ImportCatalogue />} />
                      <Route path="/code-lookup" element={<CodeLookup />} />
                      <Route path="/catalogue-matching" element={<CatalogueMatching />} />
                      <Route path="/help" element={<HelpCenter />} />
                      <Route path="/upgrade" element={<Pricing />} />
                      <Route 
                        path="/admin" 
                        element={
                          <AdminRoute>
                            <AdminDashboard />
                          </AdminRoute>
                        } 
                      />
                    </Routes>
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster 
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#0A0A0A',
                color: '#E3DDDB',
                border: '1px solid rgba(255,255,255,0.1)',
              },
            }}
          />
        </div>
      </BrowserRouter>
      </FeedbackProvider>
      </QuotaProvider>
    </AuthContext.Provider>
  );
}

export default App;
